import React, { useState } from 'react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

/**
 * XiMascot — the static mascot surface for the homepage hero.
 *
 * The character is the supplied high-resolution Xi artwork
 * (`/assets/mascot/xi-mascot-home.png`). It is intentionally isolated behind
 * `MASCOT_STATES` so the mobile app's animated assets can be dropped in later
 * without touching layout:
 *
 *   idle      → xi-mascot-home.png  (current)
 *   listening → mascot-listening.gif (supply)
 *   thinking  → mascot-thinking.gif  (supply)
 *   speaking  → mascot-speaking.gif  (supply)
 *
 * `.gif`, `.webp`, `.png` and `.svg` are rendered as <img>; `.lottie` and
 * `.json` are rendered with the already-installed dotLottie player. Nothing
 * else in the app needs to change when those files arrive.
 *
 * The 3D model is deliberately NOT used here — the homepage is a static
 * composition. The chatbot owns the interactive 3D mascot.
 */

export const XI_MASCOT_HOME_SRC = '/assets/mascot/xi-mascot-home.png';

export const MASCOT_STATES = {
  idle: XI_MASCOT_HOME_SRC,
  listening: XI_MASCOT_HOME_SRC,
  thinking: XI_MASCOT_HOME_SRC,
  speaking: XI_MASCOT_HOME_SRC,
};

/* Last-resort fallback if the supplied artwork cannot be fetched. */
const FALLBACK = '/xi/mascot-idle.png';

export default function XiMascot({
  state = 'idle',
  size = 'lg',
  platform = true,
  className = '',
  alt = 'Xi, your wellbeing companion',
}) {
  const [failed, setFailed] = useState(false);

  const src = failed ? FALLBACK : MASCOT_STATES[state] || MASCOT_STATES.idle;
  const isLottie = /\.(lottie|json)(\?|$)/i.test(src);

  return (
    <div
      className={`xi-mascot xi-mascot--${size} xi-mascot--${state} ${className}`.trim()}
      data-mascot-state={state}
    >
      <span className="xi-mascot__halo" aria-hidden="true" />

      {isLottie ? (
        <DotLottieReact
          src={src}
          loop
          autoplay
          className="xi-mascot__img"
          style={{ width: '100%', height: '100%' }}
        />
      ) : (
        <img
          className="xi-mascot__img"
          src={src}
          alt={alt}
          draggable={false}
          decoding="async"
          onError={() => setFailed(true)}
        />
      )}

      {platform && <span className="xi-mascot__platform" aria-hidden="true" />}
    </div>
  );
}
